import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatRomeDate } from '../lib/datetime';
import { api, useAuth } from '../contexts/AuthContext';
import LatestPatchNotesCard from '../components/LatestPatchNotesCard';
import DailyAveragesCard from '../components/DailyAveragesCard';
import HourlyBreakdownCard from '../components/HourlyBreakdownCard';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip
} from 'recharts';
import {
  Ticket,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Plus,
  Loader2,
  RefreshCw,
  ChevronRight,
  MapPin
} from 'lucide-react';

const statusColors = {
  aperto: '#3B82F6',
  assegnato: '#8B5CF6',
  in_lavorazione: '#06B6D4',
  in_attesa_pezzi: '#A855F7',
  completato: '#22C55E',
  chiuso: '#64748B'
};

const statusLabels = {
  aperto: 'Aperto',
  assegnato: 'Assegnato',
  in_lavorazione: 'In Lavorazione',
  in_attesa_pezzi: 'In Attesa Pezzi',
  completato: 'Completato',
  chiuso: 'Chiuso'
};

const priorityLabels = {
  urgente: 'Urgente',
  alta: 'Alta',
  media: 'Media',
  bassa: 'Bassa'
};

const HotelManagerDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [hotels, setHotels] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [hotelsRes, ticketsRes] = await Promise.all([
        api.get('/hotels'),
        api.get('/tickets?limit=500')  // dashboard ha bisogno di stats aggregate
      ]);
      setHotels(hotelsRes.data);
      // Backward-compat: nuovo formato { items, total } o vecchio array
      const tdata = ticketsRes.data;
      setTickets(Array.isArray(tdata) ? tdata : (tdata.items || []));
    } catch (err) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-900" />
      </div>
    );
  }

  const openTickets = tickets.filter(t => !['completato', 'chiuso'].includes(t.status));
  const criticalTickets = tickets.filter(t => t.priority === 'urgente' && !['completato', 'chiuso'].includes(t.status));
  const inProgressTickets = tickets.filter(t => t.status === 'in_lavorazione');
  
  // Status breakdown
  const statusBreakdown = {};
  tickets.forEach(t => {
    statusBreakdown[t.status] = (statusBreakdown[t.status] || 0) + 1;
  });
  
  const pieData = Object.entries(statusBreakdown).map(([key, value]) => ({
    name: statusLabels[key],
    value,
    color: statusColors[key]
  }));

  const hotelName = hotels.length > 0 ? hotels[0].name : 'Il tuo Hotel';

  return (
    <div className="space-y-6" data-testid="hotel-manager-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-slate-900">Dashboard</h1>
          <p className="text-slate-500 mt-1">{hotelName}</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchData} variant="outline" className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Aggiorna
          </Button>
          <Button onClick={() => navigate('/tickets')} className="bg-blue-900 hover:bg-blue-800 gap-2">
            <Plus className="w-4 h-4" />
            Nuovo Ticket
          </Button>
        </div>
      </div>

      {/* Medie giornaliere ticket + fasce orarie */}
      <DailyAveragesCard showHotels={false} extraCard={<HourlyBreakdownCard />} />

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="stat-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Ticket Totali</p>
                <p className="text-3xl font-black text-slate-900 mt-2">{tickets.length}</p>
              </div>
              <Ticket className="w-8 h-8 text-blue-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Aperti</p>
                <p className="text-3xl font-black text-amber-600 mt-2">{openTickets.length}</p>
              </div>
              <Clock className="w-8 h-8 text-amber-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500">In Lavorazione</p>
                <p className="text-3xl font-black text-cyan-600 mt-2">{inProgressTickets.length}</p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-cyan-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Critici</p>
                <p className="text-3xl font-black text-red-600 mt-2">{criticalTickets.length}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status pie chart */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg font-bold">Stato Ticket</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap gap-3 justify-center mt-4">
              {pieData.map((item) => (
                <div key={item.name} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                  <span className="text-xs text-slate-600">{item.name}: {item.value}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Quick actions */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg font-bold">Azioni Rapide</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button
              variant="outline"
              className="w-full justify-between h-14"
              onClick={() => navigate('/tickets')}
            >
              <span className="flex items-center gap-2">
                <Ticket className="w-5 h-5" />
                Visualizza tutti i ticket
              </span>
              <ChevronRight className="w-5 h-5" />
            </Button>
            
            <Button
              variant="outline"
              className="w-full justify-between h-14"
              onClick={() => navigate('/heatmap')}
            >
              <span className="flex items-center gap-2">
                <MapPin className="w-5 h-5" />
                Mappa problemi
              </span>
              <ChevronRight className="w-5 h-5" />
            </Button>
            
            <Button
              variant="outline"
              className="w-full justify-between h-14 text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={() => navigate('/tickets?priority=urgente')}
            >
              <span className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Ticket urgenti ({criticalTickets.length})
              </span>
              <ChevronRight className="w-5 h-5" />
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Recent tickets */}
      <Card className="border-slate-200">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-bold">Ticket Recenti</CardTitle>
          <Button variant="ghost" size="sm" onClick={() => navigate('/tickets')}>
            Vedi tutti
          </Button>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-64">
            <div className="space-y-3">
              {tickets.slice(0, 10).map(ticket => (
                <div
                  key={ticket.id}
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer transition-colors"
                  onClick={() => navigate(`/tickets/${ticket.id}`)}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className={`priority-${ticket.priority} text-xs`}>
                        {priorityLabels[ticket.priority]}
                      </Badge>
                      <Badge variant="secondary" className={`status-${ticket.status} text-xs`}>
                        {statusLabels[ticket.status]}
                      </Badge>
                    </div>
                    <h4 className="font-medium text-slate-900 truncate">{ticket.title}</h4>
                    <p className="text-sm text-slate-500">
                      {ticket.area} • {formatRomeDate(ticket.created_at)}
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                </div>
              ))}
              
              {tickets.length === 0 && (
                <div className="text-center py-8">
                  <CheckCircle2 className="w-12 h-12 text-green-300 mx-auto mb-4" />
                  <p className="text-slate-500">Nessun ticket presente</p>
                </div>
              )}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>

      <LatestPatchNotesCard limit={3} />
    </div>
  );
};

export default HotelManagerDashboard;
